import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Download, 
  RefreshCw, 
  Package,
  CalendarIcon,
  FileSpreadsheet,
  Layers,
  TrendingDown,
  DollarSign,
  Clock,
  AlertCircle,
  AlertTriangle,
  Filter
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useBatchTraceabilityReport } from '@/hooks/useBatchTraceability';
import { 
  exportBatchTraceabilityToExcel, 
  CONSUMPTION_TYPE_LABELS 
} from '@/services/batchTraceabilityService';
import { formatCurrency } from '@/lib/utils';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--destructive))'];

const consumptionTypeBadgeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  chipchip_sale: 'default',
  b2b_delivery: 'secondary',
  damage: 'destructive',
  adjustment: 'outline',
  other: 'outline',
};

// Fetch products for the filter dropdown
const useProducts = () => {
  return useQuery({
    queryKey: ['products-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

const BatchTraceabilityReport: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<string>('last-30-days');
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>(undefined);

  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  const { data: products } = useProducts();

  const { data: report, isLoading, isError, error, refetch } = useBatchTraceabilityReport(
    startStr,
    endStr,
    selectedProductId
  );

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    const now = new Date();
    
    switch (range) {
      case 'last-7-days':
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      case 'last-30-days':
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case 'this-month':
        setStartDate(startOfMonth(now));
        setEndDate(now);
        break;
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      default:
        break;
    }
  };

  const handleExportExcel = () => {
    if (!report) return;
    const filename = `batch_traceability_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}`;
    exportBatchTraceabilityToExcel(report, filename, { start: startStr, end: endStr });
  };

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Batch Traceability Report</h2>
            <p className="text-muted-foreground">Track inventory batches, consumption, and COGS</p>
          </div>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Report</AlertTitle>
          <AlertDescription className="mt-2">
            <p>Couldn't load batch traceability data. Please try again.</p>
            {error instanceof Error && (
              <p className="mt-1 text-sm opacity-80">Details: {error.message}</p>
            )}
            <Button onClick={() => refetch()} variant="outline" size="sm" className="mt-3">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading batch traceability data...</p>
        </div>
      </div>
    );
  }

  const summary = report?.summary || {
    totalBatches: 0,
    totalRemainingQuantity: 0,
    totalRemainingValue: 0,
    totalConsumed: 0,
    totalCogs: 0,
    avgBatchAge: 0,
  };

  // Prepare chart data for consumption by type
  const consumptionByType = report?.consumptionLog.reduce((acc, c) => {
    const type = c.consumption_type;
    if (!acc[type]) {
      acc[type] = { type, quantity: 0, value: 0 };
    }
    acc[type].quantity += c.quantity_consumed;
    acc[type].value += c.quantity_consumed * c.unit_cost_at_consumption;
    return acc;
  }, {} as Record<string, { type: string; quantity: number; value: number }>);

  const consumptionChartData = Object.values(consumptionByType || {}).map((item, idx) => ({
    name: CONSUMPTION_TYPE_LABELS[item.type] || item.type,
    quantity: item.quantity,
    value: item.value,
    fill: CHART_COLORS[idx % CHART_COLORS.length]
  }));

  // Batch age distribution
  const ageBuckets = { '0-3 days': 0, '4-7 days': 0, '8-14 days': 0, '15+ days': 0 };
  report?.batchLayers.forEach(b => {
    if (b.age_days <= 3) ageBuckets['0-3 days'] += b.remaining_quantity;
    else if (b.age_days <= 7) ageBuckets['4-7 days'] += b.remaining_quantity;
    else if (b.age_days <= 14) ageBuckets['8-14 days'] += b.remaining_quantity;
    else ageBuckets['15+ days'] += b.remaining_quantity;
  });
  
  const ageChartData = Object.entries(ageBuckets).map(([name, value], idx) => ({
    name,
    value,
    fill: idx === 3 ? 'hsl(var(--destructive))' : CHART_COLORS[idx % CHART_COLORS.length]
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Batch Traceability Report</h2>
          <p className="text-muted-foreground">Track inventory batches, consumption history, and COGS by product</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExportExcel} variant="outline" size="sm">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last-7-days">Last 7 Days</SelectItem>
                <SelectItem value="last-30-days">Last 30 Days</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Product Filter */}
            <Select 
              value={selectedProductId || 'all'} 
              onValueChange={(val) => setSelectedProductId(val === 'all' ? undefined : val)}
            >
              <SelectTrigger className="w-56">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {dateRange === 'custom' && (
              <div className="flex gap-2 items-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(startDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(endDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="ml-auto text-sm text-muted-foreground">
              Showing data from {format(startDate, 'MMM dd, yyyy')} to {format(endDate, 'MMM dd, yyyy')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPI Cards */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Batches</p>
                <p className="text-2xl font-bold">{summary.totalBatches}</p>
              </div>
              <Layers className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Remaining Stock</p>
                <p className="text-2xl font-bold">{summary.totalRemainingQuantity.toFixed(0)} kg</p>
              </div>
              <Package className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Stock Value</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalRemainingValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Consumed</p>
                <p className="text-2xl font-bold">{summary.totalConsumed.toFixed(0)} kg</p>
              </div>
              <TrendingDown className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total COGS</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalCogs)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-secondary-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Batch Age</p>
                <p className="text-2xl font-bold">{summary.avgBatchAge.toFixed(1)} days</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="batches">Batch Layers</TabsTrigger>
          <TabsTrigger value="cogs">COGS by Product</TabsTrigger>
          <TabsTrigger value="consumption">Consumption Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Consumption by Type */}
            <Card>
              <CardHeader>
                <CardTitle>Consumption by Type</CardTitle>
                <CardDescription>How inventory was consumed during the period</CardDescription>
              </CardHeader>
              <CardContent>
                {consumptionChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={consumptionChartData}
                        dataKey="quantity"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {consumptionChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => [`${value.toFixed(2)} kg`, 'Quantity']} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No consumption data for this period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Batch Age Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Batch Age Distribution</CardTitle>
                <CardDescription>Remaining inventory by age (freshness)</CardDescription>
              </CardHeader>
              <CardContent>
                {report?.batchLayers && report.batchLayers.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ageChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => [`${value.toFixed(2)} kg`, 'Remaining']} />
                      <Bar dataKey="value" name="Quantity (kg)">
                        {ageChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No batch data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Aging Warning */}
          {report?.batchLayers.some(b => b.age_days > 7) && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Aging Inventory Alert</AlertTitle>
              <AlertDescription>
                {report.batchLayers.filter(b => b.age_days > 7).length} batch(es) are older than 7 days.
                Consider prioritizing these for sales to minimize spoilage risk.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="batches">
          <Card>
            <CardHeader>
              <CardTitle>Active Batch Layers</CardTitle>
              <CardDescription>Current inventory batches with remaining quantities (FIFO order)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Initial</TableHead>
                      <TableHead className="text-right">Consumed</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead className="text-right">Age</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report?.batchLayers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          No active batch layers found
                        </TableCell>
                      </TableRow>
                    ) : (
                      report?.batchLayers.map((batch) => {
                        const consumedPercent = batch.initial_quantity > 0 
                          ? ((batch.consumed_quantity / batch.initial_quantity) * 100) 
                          : 0;
                        return (
                          <TableRow key={batch.batch_layer_id}>
                            <TableCell className="font-medium">{batch.product_name}</TableCell>
                            <TableCell className="text-right font-mono">
                              {batch.initial_quantity.toFixed(1)} kg
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {batch.consumed_quantity.toFixed(1)} kg
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold">
                              {batch.remaining_quantity.toFixed(1)} kg
                            </TableCell>
                            <TableCell className="w-32">
                              <Progress value={consumedPercent} className="h-2" />
                              <span className="text-xs text-muted-foreground">
                                {consumedPercent.toFixed(0)}% consumed
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(batch.unit_cost)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(batch.total_remaining_value)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {format(new Date(batch.received_date), 'MMM d')}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant={batch.age_days > 7 ? 'destructive' : batch.age_days > 3 ? 'secondary' : 'outline'}>
                                {batch.age_days}d
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cogs">
          <Card>
            <CardHeader>
              <CardTitle>COGS by Product</CardTitle>
              <CardDescription>Cost of Goods Sold breakdown by product for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {report?.cogsSummary && report.cogsSummary.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={report.cogsSummary} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                      <YAxis 
                        dataKey="product_name" 
                        type="category" 
                        width={120}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value: number, name: string) => [
                          name === 'total_cogs' ? formatCurrency(value) : `${value.toFixed(2)} kg`,
                          name === 'total_cogs' ? 'COGS' : 'Quantity'
                        ]} 
                      />
                      <Bar dataKey="total_cogs" fill="hsl(var(--primary))" name="COGS" />
                    </BarChart>
                  </ResponsiveContainer>
                  
                  <div className="rounded-md border mt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Qty Consumed</TableHead>
                          <TableHead className="text-right">Total COGS</TableHead>
                          <TableHead className="text-right">Avg Unit Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.cogsSummary.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-right font-mono">
                              {item.total_quantity.toFixed(2)} kg
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold">
                              {formatCurrency(item.total_cogs)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {formatCurrency(item.avg_unit_cost)}/kg
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No COGS data for this period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="consumption">
          <Card>
            <CardHeader>
              <CardTitle>Consumption Log</CardTitle>
              <CardDescription>Detailed log of all batch consumption events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty Consumed</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report?.consumptionLog.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No consumption records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      report?.consumptionLog.slice(0, 100).map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {format(new Date(log.reference_date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell className="font-medium">{log.product_name}</TableCell>
                          <TableCell>
                            <Badge variant={consumptionTypeBadgeVariants[log.consumption_type] || 'outline'}>
                              {CONSUMPTION_TYPE_LABELS[log.consumption_type] || log.consumption_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {log.quantity_consumed.toFixed(2)} kg
                          </TableCell>
                          <TableCell className="text-right font-mono text-muted-foreground">
                            {formatCurrency(log.unit_cost_at_consumption)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {formatCurrency(log.quantity_consumed * log.unit_cost_at_consumption)}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground">
                            {log.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {report?.consumptionLog && report.consumptionLog.length > 100 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Showing first 100 of {report.consumptionLog.length} records. Export to Excel for full data.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BatchTraceabilityReport;
